import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  createMaterial,
  getMaterialBom,
  getMaterialRouting,
  getMaterialVariantDimensions,
  listMaterialsPaged,
  replaceMaterialBom,
  replaceMaterialRouting,
  replaceMaterialVariantDimensions,
  updateMaterial,
} from "../api/materials";
import { listVariantDimensions } from "../api/variantDimensions";
import { listWorkCenterGroups } from "../api/workCenterGroups";

const MATERIAL_PICKER_LIMIT = 100;
const MATERIAL_PICKER_PREFETCH_PAGES = 2; // user-requested: ensure page 2 is available
const MATERIAL_PICKER_MAX_PAGES = 20; // safety cap
const LOAD_MORE_VALUE = "__load_more__";

function MaterialModal({ data, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState("general"); // general | variant | bom | routing
  const nextRowKeyRef = useRef(1);

  const makeRowKey = (prefix) => `${prefix}-${Date.now()}-${nextRowKeyRef.current++}`;
  const ensureRowKey = (row, prefix) => {
    if (row && typeof row === "object" && row.__row_key) return row;
    const id = Number(row?.id);
    const stable = Number.isFinite(id) ? `${prefix}-id-${id}` : makeRowKey(prefix);
    return { ...(row || {}), __row_key: stable };
  };

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [materialType, setMaterialType] = useState("raw");
  const [unit, setUnit] = useState("adet");
  const [notes, setNotes] = useState("");
  const [hasManufacturingSource, setHasManufacturingSource] = useState(false);
  const [manufacturingSource, setManufacturingSource] = useState(1); // 1 = Bobin, 2 = Fason

  const [allDimensions, setAllDimensions] = useState([]);
  const [selectedDimensionIds, setSelectedDimensionIds] = useState([]);
  const [requiredByDimensionId, setRequiredByDimensionId] = useState({});
  const [loadingVariantTemplate, setLoadingVariantTemplate] = useState(false);

  const [allMaterials, setAllMaterials] = useState([]);
  const [materialsPage, setMaterialsPage] = useState(0);
  const [materialsTotal, setMaterialsTotal] = useState(0);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingBom, setLoadingBom] = useState(false);
  const [savingBom, setSavingBom] = useState(false);
  const [bomBaseQty, setBomBaseQty] = useState(1);
  const [bomItems, setBomItems] = useState([]);

  const [allWorkCenterGroups, setAllWorkCenterGroups] = useState([]);
  const [loadingRouting, setLoadingRouting] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [routingHeader, setRoutingHeader] = useState(null);
  const [routingOperations, setRoutingOperations] = useState([]);

  useEffect(() => {
    if (!data) return;
    setCode(data.code || "");
    setName(data.name || "");
    setMaterialType(data.material_type || "raw");
    setUnit(data.unit || "adet");
    setNotes(data.notes || "");
    setHasManufacturingSource(!!(data.has_manufacturing_source === 1 || data.has_manufacturing_source === true));
    setManufacturingSource(data.manufacturing_source === 2 ? 2 : 1);
    setActiveTab("general");
  }, [data]);

  // Load support dimensions once
  useEffect(() => {
    listVariantDimensions()
      .then(setAllDimensions)
      .catch((err) => {
        console.error("VARIANT DIMENSIONS LOAD ERROR", err);
        // non-blocking; modal can still be used
      });
  }, []);

  async function loadMoreMaterials({ reset = false } = {}) {
    if (loadingMaterials) return;
    setLoadingMaterials(true);
    try {
      const nextPage = reset ? 1 : materialsPage + 1;
      if (!Number.isFinite(nextPage) || nextPage <= 0) return;
      if (!reset && materialsTotal > 0 && allMaterials.length >= materialsTotal) return;
      if (!reset && nextPage > MATERIAL_PICKER_MAX_PAGES) return;

      const data = await listMaterialsPaged({ page: nextPage, limit: MATERIAL_PICKER_LIMIT });
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = Number(data?.total || 0);

      setMaterialsTotal(Number.isFinite(total) ? total : 0);
      setMaterialsPage(nextPage);
      setAllMaterials((prev) => {
        const merged = reset ? [] : Array.isArray(prev) ? [...prev] : [];
        const seen = new Set(merged.map((m) => Number(m?.id)).filter((x) => Number.isFinite(x)));
        for (const it of items) {
          const id = Number(it?.id);
          if (!Number.isFinite(id)) continue;
          if (seen.has(id)) continue;
          seen.add(id);
          merged.push(it);
        }
        return merged;
      });
    } catch (err) {
      console.error("MATERIALS LOAD ERROR", err);
      if (reset) {
        setAllMaterials([]);
        setMaterialsPage(0);
        setMaterialsTotal(0);
      }
    } finally {
      setLoadingMaterials(false);
    }
  }

  // Load materials (for BOM component selection) with prefetch pages (1..2)
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      await loadMoreMaterials({ reset: true });
      for (let i = 2; i <= MATERIAL_PICKER_PREFETCH_PAGES; i += 1) {
        if (cancelled) return;
        // sequential to keep state consistent
        // eslint-disable-next-line no-await-in-loop
        await loadMoreMaterials({ reset: false });
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const materialsHasMore = materialsTotal ? allMaterials.length < materialsTotal : true;
  const sortedMaterials = useMemo(() => {
    const items = Array.isArray(allMaterials) ? [...allMaterials] : [];
    items.sort((a, b) => String(a?.code || "").localeCompare(String(b?.code || ""), "tr-TR"));
    return items;
  }, [allMaterials]);

  // Load work center groups once (for Routing)
  useEffect(() => {
    listWorkCenterGroups({ includeInactive: false })
      .then((items) => setAllWorkCenterGroups(items || []))
      .catch((err) => {
        console.error("WORK CENTER GROUPS LOAD ERROR", err);
        setAllWorkCenterGroups([]);
      });
  }, []);

  // Load material's template when editing
  useEffect(() => {
    if (!data?.id) {
      setSelectedDimensionIds([]);
      setRequiredByDimensionId({});
      return;
    }

    setLoadingVariantTemplate(true);
    getMaterialVariantDimensions(data.id)
      .then((items) => {
        const ordered = [...items].sort(
          (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
        );
        setSelectedDimensionIds(ordered.map((x) => Number(x.dimension_id)));
        const req = {};
        for (const x of ordered) req[Number(x.dimension_id)] = !!x.required;
        setRequiredByDimensionId(req);
      })
      .catch((err) => {
        console.error("MATERIAL VARIANT TEMPLATE LOAD ERROR", err);
      })
      .finally(() => setLoadingVariantTemplate(false));
  }, [data?.id]);

  // Load BOM when editing (use manufacturingSource when has_manufacturing_source)
  useEffect(() => {
    if (!data?.id) {
      setBomBaseQty(1);
      setBomItems([]);
      return;
    }

    setLoadingBom(true);
    const mfgSource = data?.has_manufacturing_source ? manufacturingSource : 1;
    getMaterialBom(data.id, { manufacturingSource: mfgSource })
      .then((res) => {
        const bom = res?.bom || null;
        const items = Array.isArray(res?.items) ? res.items : [];
        setBomBaseQty(Number(bom?.base_qty || 1));
        setBomItems(items.map((x) => ensureRowKey(x, "bom")));
      })
      .catch((err) => {
        console.error("BOM LOAD ERROR", err);
        setBomBaseQty(1);
        setBomItems([]);
      })
      .finally(() => setLoadingBom(false));
  }, [data?.id, data?.has_manufacturing_source, manufacturingSource]);

  // Load Routing when editing (use manufacturingSource when has_manufacturing_source)
  useEffect(() => {
    if (!data?.id) {
      setRoutingHeader(null);
      setRoutingOperations([]);
      return;
    }

    setLoadingRouting(true);
    const mfgSource = data?.has_manufacturing_source ? manufacturingSource : 1;
    getMaterialRouting(data.id, { manufacturingSource: mfgSource })
      .then((res) => {
        setRoutingHeader(res?.routing || null);
        const ops = Array.isArray(res?.operations) ? res.operations : [];
        setRoutingOperations(ops.map((x) => ensureRowKey(x, "rt")));
      })
      .catch((err) => {
        console.error("ROUTING LOAD ERROR", err);
        setRoutingHeader(null);
        setRoutingOperations([]);
      })
      .finally(() => setLoadingRouting(false));
  }, [data?.id, data?.has_manufacturing_source, manufacturingSource]);

  function toggleDimension(id) {
    const dimId = Number(id);
    setSelectedDimensionIds((prev) => {
      if (prev.includes(dimId)) return prev.filter((x) => x !== dimId);
      return [...prev, dimId];
    });
    setRequiredByDimensionId((prev) => {
      // default required=false when newly selected
      if (prev[dimId] === undefined) return { ...prev, [dimId]: false };
      return prev;
    });
  }

  function toggleRequired(id) {
    const dimId = Number(id);
    setRequiredByDimensionId((prev) => ({ ...prev, [dimId]: !prev[dimId] }));
  }

  async function save() {
    if (!code || !name || !unit) {
      alert("Kod, ad ve birim zorunlu");
      return;
    }

    const payload = {
      code,
      name,
      material_type: materialType,
      unit,
      notes: notes || null,
      has_manufacturing_source: hasManufacturingSource,
    };

    try {
      const res = data ? await updateMaterial(data.id, payload) : await createMaterial(payload);
      const materialId = data?.id ? data.id : Number(res?.id);

      // Save variant template right after material save
      if (materialId) {
        const items = selectedDimensionIds.map((dimensionId, idx) => ({
          dimension_id: Number(dimensionId),
          required: !!requiredByDimensionId[Number(dimensionId)],
          sort_order: idx + 1,
        }));

        try {
          await replaceMaterialVariantDimensions(materialId, items);
        } catch (err2) {
          console.error("MATERIAL VARIANT TEMPLATE SAVE ERROR", err2);
          const msg =
            err2?.response?.data?.error?.message ||
            err2?.message ||
            "Varyant şablonu kaydedilemedi";
          alert(`Malzeme kaydedildi ama varyant şablonu kaydedilemedi.\n\n${msg}`);
          return;
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error("MATERIAL SAVE ERROR", err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Malzeme kaydedilemedi";
      alert(msg);
    }
  }

  function addBomLine() {
    setBomItems((prev) => {
      const maxLineNo = prev.reduce((acc, x) => Math.max(acc, Number(x.line_no || 0)), 0);
      const nextLineNo = Math.max(10, Math.ceil((maxLineNo + 10) / 10) * 10);
      return [
        ...prev,
        {
          __row_key: makeRowKey("bom"),
          line_no: nextLineNo,
          component_material_id: "",
          component_material_variant_id: null,
          qty_per_base: 1,
          scrap_rate: 0,
          issue_policy: "backflush",
          issue_at_step: null,
        },
      ];
    });
  }

  function updateBomItem(idx, patch) {
    setBomItems((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }

  function removeBomItem(idx) {
    setBomItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function saveBom() {
    if (!data?.id) {
      alert("Önce malzemeyi kaydedin (BOM için id gerekli).");
      return;
    }
    const baseQtyNum = Number(bomBaseQty);
    if (!Number.isFinite(baseQtyNum) || baseQtyNum <= 0) {
      alert("Base Qty > 0 olmalı");
      return;
    }

    // validate + normalize items
    const items = (bomItems || []).map((x, idx) => ({
      line_no: Number(x.line_no ?? (idx + 1) * 10),
      component_material_id: Number(x.component_material_id),
      component_material_variant_id:
        x.component_material_variant_id === null || x.component_material_variant_id === undefined
          ? null
          : Number(x.component_material_variant_id),
      qty_per_base: Number(x.qty_per_base),
      scrap_rate:
        x.scrap_rate === null || x.scrap_rate === undefined || x.scrap_rate === ""
          ? null
          : Number(x.scrap_rate),
      issue_policy: x.issue_policy ? String(x.issue_policy) : "backflush",
      issue_at_step:
        x.issue_at_step === null || x.issue_at_step === undefined || x.issue_at_step === ""
          ? null
          : Number(x.issue_at_step),
    }));

    if (!items.length) {
      alert("En az 1 BOM satırı ekleyin.");
      return;
    }
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (!Number.isFinite(it.line_no) || it.line_no <= 0) {
        alert(`Satır no hatalı (index ${i})`);
        return;
      }
      if (!Number.isFinite(it.component_material_id)) {
        alert(`Komponent malzeme seçin (index ${i})`);
        return;
      }
      if (!Number.isFinite(it.qty_per_base) || it.qty_per_base <= 0) {
        alert(`Miktar > 0 olmalı (index ${i})`);
        return;
      }
      if (it.issue_at_step !== null && (!Number.isFinite(it.issue_at_step) || it.issue_at_step <= 0)) {
        alert(`issue_at_step (Op No) hatalı (index ${i})`);
        return;
      }
    }

    setSavingBom(true);
    try {
      const mfgSource = data?.has_manufacturing_source ? manufacturingSource : 1;
      await replaceMaterialBom(data.id, { base_qty: baseQtyNum, items }, { manufacturingSource: mfgSource });
      alert("BOM kaydedildi");
      const fresh = await getMaterialBom(data.id, { manufacturingSource: mfgSource });
      setBomBaseQty(Number(fresh?.bom?.base_qty || 1));
      setBomItems((Array.isArray(fresh?.items) ? fresh.items : []).map((x) => ensureRowKey(x, "bom")));
    } catch (err) {
      console.error("BOM SAVE ERROR", err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "BOM kaydedilemedi";
      alert(msg);
    } finally {
      setSavingBom(false);
    }
  }

  function addRoutingLine() {
    setRoutingOperations((prev) => {
      const maxOpNo = prev.reduce((acc, x) => Math.max(acc, Number(x.op_no || 0)), 0);
      const nextOpNo = Math.max(10, Math.ceil((maxOpNo + 10) / 10) * 10);
      return [
        ...prev,
        {
          __row_key: makeRowKey("rt"),
          op_no: nextOpNo,
          operation_code: "",
          description: "",
          work_center_group_id: "",
          output_material_id: "",
          setup_min: 0,
          machine_min: 0,
          labor_min: 0,
        },
      ];
    });
  }

  function updateRoutingOp(rowKey, patch) {
    const k = String(rowKey || "");
    if (!k) return;
    setRoutingOperations((prev) =>
      prev.map((x) => (String(x.__row_key || "") === k ? { ...x, ...patch } : x))
    );
  }

  function removeRoutingOp(idx) {
    setRoutingOperations((prev) => prev.filter((_, i) => i !== idx));
  }

  async function saveRouting() {
    if (!data?.id) {
      alert("Önce malzemeyi kaydedin (Rota için id gerekli).");
      return;
    }

    const parsePositiveInt = (v) => {
      if (v === null || v === undefined || v === "") return null;
      if (typeof v === "number" && Number.isFinite(v) && v > 0) return Math.trunc(v);
      const s = String(v).trim();
      if (!s) return null;
      const n = Number.parseInt(s, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const ops = (routingOperations || []).map((x, idx) => ({
      op_no: Number(x.op_no ?? (idx + 1) * 10),
      operation_code: x.operation_code ? String(x.operation_code) : "",
      description: x.description ? String(x.description) : null,
      work_center_group_id: parsePositiveInt(x.work_center_group_id),
      output_material_id:
        x.output_material_id === null || x.output_material_id === undefined || x.output_material_id === ""
          ? null
          : Number(x.output_material_id),
      output_material_variant_id: null,
      setup_min: Number(x.setup_min ?? 0),
      machine_min: Number(x.machine_min ?? 0),
      labor_min: Number(x.labor_min ?? 0),
    }));

    if (!ops.length) {
      alert("En az 1 operasyon ekleyin.");
      return;
    }

    for (let i = 0; i < ops.length; i += 1) {
      const op = ops[i];
      if (!Number.isFinite(op.op_no) || op.op_no <= 0) {
        alert(`Op No hatalı (index ${i})`);
        return;
      }
      if (!op.work_center_group_id || !Number.isFinite(op.work_center_group_id)) {
        alert(`Makine grubu seçin (index ${i})`);
        return;
      }
      if (!Number.isFinite(op.setup_min) || op.setup_min < 0) {
        alert(`setup_min >= 0 olmalı (index ${i})`);
        return;
      }
      if (!Number.isFinite(op.machine_min) || op.machine_min < 0) {
        alert(`machine_min >= 0 olmalı (index ${i})`);
        return;
      }
      if (!Number.isFinite(op.labor_min) || op.labor_min < 0) {
        alert(`labor_min >= 0 olmalı (index ${i})`);
        return;
      }
      if (op.output_material_id !== null && !Number.isFinite(op.output_material_id)) {
        alert(`output_material_id hatalı (index ${i})`);
        return;
      }
      if (op.output_material_variant_id !== null && !Number.isFinite(op.output_material_variant_id)) {
        alert(`output_material_variant_id hatalı (index ${i})`);
        return;
      }
    }

    setSavingRouting(true);
    try {
      const mfgSource = data?.has_manufacturing_source ? manufacturingSource : 1;
      await replaceMaterialRouting(data.id, { operations: ops }, { manufacturingSource: mfgSource });
      alert("Rota kaydedildi");
      try {
        const fresh = await getMaterialRouting(data.id, { manufacturingSource: mfgSource });
        setRoutingHeader(fresh?.routing || null);
        setRoutingOperations(
          (Array.isArray(fresh?.operations) ? fresh.operations : []).map((x) => ensureRowKey(x, "rt"))
        );
      } catch (refreshErr) {
        console.error("ROUTING REFRESH ERROR", refreshErr);
        // non-blocking: save succeeded; keep user edits in UI
      }
    } catch (err) {
      console.error("ROUTING SAVE ERROR", err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Rota kaydedilemedi";
      alert(msg);
    } finally {
      setSavingRouting(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {data ? "Malzeme Düzenle" : "Yeni Malzeme"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Genel bilgiler, varyant şablonu, BOM ve rota ayarları.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="general" label="Genel" />
            <Tab value="variant" label="Varyant Şablonu" />
            <Tab value="bom" label="Ürün Ağacı (BOM)" />
            <Tab value="routing" label="Rota" />
          </Tabs>
        </Box>

        {activeTab === "general" && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Malzeme Kodu"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Açıklama / İsim"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Tip"
                  value={materialType}
                  onChange={(e) => setMaterialType(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="raw">Hammadde</MenuItem>
                  <MenuItem value="semi_finished">Yarı Mamul</MenuItem>
                  <MenuItem value="finished">Mamul</MenuItem>
                  <MenuItem value="consumable">Sarf</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Birim (adet, kg, m...)"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Not (opsiyonel)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>

              {materialType === "finished" && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasManufacturingSource}
                        onChange={(e) => setHasManufacturingSource(e.target.checked)}
                      />
                    }
                    label="Üretim kaynağı kullan (Bobin/Fason)"
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {activeTab === "variant" && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Varyant Şablonu</Typography>
            {!data?.id ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Not: Yeni malzeme kaydedildiğinde bu şablon da kaydedilir.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Bu malzeme için varyant anahtarlarını seçin ve zorunlu alanları işaretleyin.
              </Typography>
            )}

            {loadingVariantTemplate ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Şablon yükleniyor...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                {!allDimensions.length ? (
                  <Alert severity="info">
                    Varyant anahtarı bulunamadı. Önce “Destek Tabloları”ndan ekleyin.
                  </Alert>
                ) : (
                  <Box>
                    {allDimensions.map((d) => {
                      const dimId = Number(d.id);
                      const checked = selectedDimensionIds.includes(dimId);
                      return (
                        <Box
                          key={dimId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 0.5,
                          }}
                        >
                          <FormControlLabel
                            sx={{ flex: 1, m: 0 }}
                            control={
                              <Checkbox checked={checked} onChange={() => toggleDimension(dimId)} />
                            }
                            label={
                              <Box>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {d.label}{" "}
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    ({d.key})
                                  </Typography>
                                </Typography>
                              </Box>
                            }
                          />

                          <FormControlLabel
                            sx={{ m: 0, opacity: checked ? 1 : 0.5 }}
                            control={
                              <Checkbox
                                checked={!!requiredByDimensionId[dimId]}
                                disabled={!checked}
                                onChange={() => toggleRequired(dimId)}
                              />
                            }
                            label="Zorunlu"
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}

        {activeTab === "bom" && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {!data?.id ? (
              <Alert severity="info">BOM tanımlamak için önce malzemeyi kaydedin.</Alert>
            ) : loadingBom ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  BOM yükleniyor...
                </Typography>
              </Box>
            ) : (
              <Box>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  {data?.has_manufacturing_source && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Üretim kaynağı"
                        value={manufacturingSource}
                        onChange={(e) => setManufacturingSource(Number(e.target.value))}
                        fullWidth
                      >
                        <MenuItem value={1}>Bobin</MenuItem>
                        <MenuItem value={2}>Fason</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Base Qty"
                      type="number"
                      value={bomBaseQty}
                      onChange={(e) => setBomBaseQty(e.target.value)}
                      inputProps={{ step: "0.0001" }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={data?.has_manufacturing_source ? 4 : 8} sx={{ display: "flex", justifyContent: { md: "flex-end" } }}>
                    <Button type="button" variant="outlined" onClick={addBomLine}>
                      + Satır Ekle
                    </Button>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {!bomItems.length ? (
                  <Alert severity="info">Henüz BOM satırı yok.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 110 }}>Satır</TableCell>
                          <TableCell>Komponent</TableCell>
                          <TableCell sx={{ width: 110 }}>Op No</TableCell>
                          <TableCell sx={{ width: 140 }}>Qty/Base</TableCell>
                          <TableCell sx={{ width: 140 }}>Fire (0..1)</TableCell>
                          <TableCell sx={{ width: 80 }} align="right">
                            Sil
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bomItems.map((it, idx) => {
                          const componentId = it.component_material_id ? String(it.component_material_id) : "";
                          return (
                            <TableRow key={String(it.__row_key || idx)} hover>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={it.line_no ?? ""}
                                  onChange={(e) => updateBomItem(idx, { line_no: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  value={componentId}
                                  onChange={(e) => {
                                    const v = String(e.target.value || "");
                                    if (v === LOAD_MORE_VALUE) {
                                      loadMoreMaterials({ reset: false });
                                      return;
                                    }
                                    updateBomItem(idx, { component_material_id: v });
                                  }}
                                  fullWidth
                                >
                                  <MenuItem value="">
                                    <em>Komponent seç...</em>
                                  </MenuItem>
                                  {sortedMaterials.map((m) => (
                                    <MenuItem key={m.id} value={String(m.id)}>
                                      {m.code} - {m.name}
                                    </MenuItem>
                                  ))}
                                  {materialsHasMore ? (
                                    <MenuItem value={LOAD_MORE_VALUE}>
                                      {loadingMaterials ? "Yükleniyor..." : "Daha fazla yükle (sayfa 2+)"}
                                    </MenuItem>
                                  ) : null}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={it.issue_at_step ?? ""}
                                  onChange={(e) => updateBomItem(idx, { issue_at_step: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.000001" }}
                                  value={it.qty_per_base ?? ""}
                                  onChange={(e) => updateBomItem(idx, { qty_per_base: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.000001" }}
                                  value={it.scrap_rate ?? ""}
                                  onChange={(e) => updateBomItem(idx, { scrap_rate: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  type="button"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => removeBomItem(idx)}
                                >
                                  Sil
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button type="button" variant="contained" disabled={savingBom} onClick={saveBom}>
                    {savingBom ? "Kaydediliyor..." : "BOM Kaydet"}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {activeTab === "routing" && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {!data?.id ? (
              <Alert severity="info">Rota tanımlamak için önce malzemeyi kaydedin.</Alert>
            ) : loadingRouting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Rota yükleniyor...
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                  {data?.has_manufacturing_source && (
                    <TextField
                      select
                      label="Üretim kaynağı"
                      value={manufacturingSource}
                      onChange={(e) => setManufacturingSource(Number(e.target.value))}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={1}>Bobin</MenuItem>
                      <MenuItem value={2}>Fason</MenuItem>
                    </TextField>
                  )}
                  <Typography sx={{ fontWeight: 800, flex: 1 }}>
                    Operasyonlar{" "}
                    {routingHeader?.version ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        (v{routingHeader.version})
                      </Typography>
                    ) : null}
                  </Typography>
                  <Button type="button" variant="outlined" onClick={addRoutingLine}>
                    + Operasyon Ekle
                  </Button>
                </Box>

                {!routingOperations.length ? (
                  <Alert severity="info">Henüz rota operasyonu yok.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 90 }}>Op No</TableCell>
                          <TableCell sx={{ width: 140 }}>Kod</TableCell>
                          <TableCell sx={{ minWidth: 220 }}>Açıklama</TableCell>
                          <TableCell sx={{ width: 220 }}>Makine Grubu</TableCell>
                          <TableCell sx={{ width: 280 }}>Çıktı Malzemesi</TableCell>
                          <TableCell sx={{ width: 120 }}>Setup</TableCell>
                          <TableCell sx={{ width: 120 }}>Makine</TableCell>
                          <TableCell sx={{ width: 120 }}>İşçilik</TableCell>
                          <TableCell sx={{ width: 90 }} align="right">
                            Sil
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {routingOperations.map((op, idx) => {
                          const wcgId = op.work_center_group_id ? String(op.work_center_group_id) : "";
                          const outMatId = op.output_material_id ? String(op.output_material_id) : "";
                          const rowKey = String(op.__row_key || idx);

                          return (
                            <TableRow key={rowKey} hover>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="text"
                                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                  value={op.op_no === null || op.op_no === undefined ? "" : String(op.op_no)}
                                  onChange={(e) => updateRoutingOp(rowKey, { op_no: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={op.operation_code === null || op.operation_code === undefined ? "" : String(op.operation_code)}
                                  onChange={(e) => updateRoutingOp(rowKey, { operation_code: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={op.description ?? ""}
                                  onChange={(e) => updateRoutingOp(rowKey, { description: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  value={wcgId}
                                  onChange={(e) => updateRoutingOp(rowKey, { work_center_group_id: e.target.value })}
                                  fullWidth
                                >
                                  <MenuItem value="">
                                    <em>Makine grubu seç...</em>
                                  </MenuItem>
                                  {allWorkCenterGroups.map((wg) => (
                                    <MenuItem key={wg.id} value={String(wg.id)}>
                                      {wg.code} — {wg.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  value={outMatId}
                                  onChange={(e) => {
                                    const v = String(e.target.value || "");
                                    if (v === LOAD_MORE_VALUE) {
                                      loadMoreMaterials({ reset: false });
                                      return;
                                    }
                                    updateRoutingOp(rowKey, { output_material_id: v });
                                  }}
                                  fullWidth
                                >
                                  <MenuItem value="">
                                    <em>Seç...</em>
                                  </MenuItem>
                                  {sortedMaterials.map((m) => (
                                    <MenuItem key={m.id} value={String(m.id)}>
                                      {m.code} - {m.name}
                                    </MenuItem>
                                  ))}
                                  {materialsHasMore ? (
                                    <MenuItem value={LOAD_MORE_VALUE}>
                                      {loadingMaterials ? "Yükleniyor..." : "Daha fazla yükle (sayfa 2+)"}
                                    </MenuItem>
                                  ) : null}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.0001" }}
                                  value={op.setup_min ?? 0}
                                  onChange={(e) => updateRoutingOp(rowKey, { setup_min: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.0001" }}
                                  value={op.machine_min ?? 0}
                                  onChange={(e) => updateRoutingOp(rowKey, { machine_min: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.0001" }}
                                  value={op.labor_min ?? 0}
                                  onChange={(e) => updateRoutingOp(rowKey, { labor_min: e.target.value })}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  type="button"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => removeRoutingOp(idx)}
                                >
                                  Sil
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button type="button" variant="contained" disabled={savingRouting} onClick={saveRouting}>
                    {savingRouting ? "Kaydediliyor..." : "Rota Kaydet"}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        <Button onClick={save} variant="contained">
          Kaydet
        </Button>
      </DialogActions>

    </Dialog>
  );
}

export default MaterialModal;

